import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconBrandJourneysGradient } from '@components/icons/brand-journeys-gradient';
import { IconGear } from '@components/icons/gear';
import { useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useDismissJourneysBanner } from './hooks';
import { JobStageInfo, TriggersSidebar, TriggersSidebarProps } from './triggers-sidebar';

import { useIsAppsAdmin } from '../hooks';

export type JourneysBannerProps = {
  jobId: number;
  jobStages: JobStageInfo[];
};

/**
 * Prompts connecting a job to a journey. Rendered only when
 * useShowJourneysBanner says so — the page owns that decision because the
 * pipeline re-measures its height when the banner appears.
 *
 * Dismissal is stored per job (Job.show_journeys_banner), so dismissing hides
 * the banner for every recruiter on this job, on every device; the user's
 * other jobs are unaffected.
 */
export const JourneysBanner = ({ jobId, jobStages }: JourneysBannerProps) => {
  const { t } = useScopedTranslate(locale);
  const isAdmin = useIsAppsAdmin();
  const dismiss = useDismissJourneysBanner(jobId);

  const showSidebar = useSidebar(TriggersSidebar, {
    variant: 'REGULAR',
    hasBackdrop: true,
  });

  const handleConfigure = () => {
    const sidebarProps: TriggersSidebarProps = { jobId, jobStages, isAdmin };
    showSidebar(sidebarProps);
  };

  return (
    <BannerCard
      variant="transparent"
      spaceX={3}
      spaceY={2}
      style={aiSurface}
      data-cy="journeys-automation-banner"
    >
      <Stack orientation="horizontal" align="center" space={2}>
        <IconBrandJourneysGradient size={24} style={noShrink} />
        <TextBlock>
          <Text font="semibold14" text={t('title')} />
          <Text font="regular12" color="textSecondary" text={t('description')} />
        </TextBlock>
        <ActionButton
          variant="secondary"
          size="sm"
          font="semibold12"
          text={t('dismiss')}
          dataCy="journeys-banner-close"
          onClick={() => dismiss.mutate()}
        />
        <ActionButton
          variant="primary-blue"
          size="sm"
          font="semibold12"
          iconLeft={<IconGear size={24} />}
          text={t('configure')}
          dataCy="journeys-banner-configure"
          onClick={handleConfigure}
        />
      </Stack>
    </BannerCard>
  );
};

/**
 * The design's surface is the AI teal→violet wash behind a teal hairline.
 * `Card` has no AI variant, so the two paint properties are set inline —
 * `Card` merges `style` onto the element, which is the only reliable way to
 * beat its own `[data-variant]` rules from the outside.
 */
const aiSurface = {
  background: theme.gradient.aiSurface10,
  borderColor: theme.color.elementTeal100,
};

/**
 * The banner shares the pipeline's gutter so its left edge lines up with the
 * first stage column rather than running to the page edge — `Container` in
 * pages/Job/pipeline/pipeline.tsx sets that 24px inset.
 *
 * The 16px above is the banner's own: the filter bar it sits under puts its
 * matching padding *inside* its border-bottom, so that border leaves no gap
 * behind it and the banner would otherwise sit flush against the rule.
 */
const BannerCard = styled(Card)({
  marginTop: pxToRem(16),
  marginLeft: pxToRem(24),
  marginRight: pxToRem(24),
});

// A one-line banner: the text column is the only element allowed to give way.
const noShrink = { flexShrink: 0 };

const ActionButton = styled(Button)(noShrink);

const TextBlock = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
});

const locale = {
  en: {
    title: 'Automate journeys from this hiring pipeline',
    description: 'Start a journey when an applicant reaches a stage or receives a final outcome.',
    configure: 'Configure',
    dismiss: 'Dismiss',
  },
  is: {
    title: 'Ræstu Journey-ferli sjálfkrafa úr ráðningarferlinu',
    description:
      'Ræstu Journey-ferli sjálfkrafa þegar umsækjandi er færður í tiltekinn dálk, er ráðinn eða fær höfnun.',
    configure: 'Stilla',
    dismiss: 'Loka',
  },
};
